import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CircleDashed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/lib/constants';
import {
  BUDGET_CLASSIFY_ACTION,
  BUDGET_VIEW_CATEGORIES,
  budgetUnassignedAmount,
  budgetUnassignedCount,
} from '@/lib/translations';

export interface UnassignedStripProps {
  /** The summary's `unassigned`: money moving in either direction in categories that carry no classification. */
  amount: string;
  /** Unclassified categories, counted from the category list — the API cannot say which ones hold the money. */
  categoryCount: number;
}

// Neutral on purpose (TRD §8.5): the PageHeader eyebrow already spends this screen's amber, Guard Red is for
// destructive actions, and emerald / expense red are reserved for income and expense.
export const UnassignedStrip: React.FC<UnassignedStripProps> = ({ amount, categoryCount }) => {
  const hasCategories = categoryCount > 0;

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-muted/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        {/* An open circle — not counted yet — rather than a warning sign: nothing here is an error. */}
        <CircleDashed className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="min-w-0 space-y-0.5 text-sm">
          <p className="text-foreground">{budgetUnassignedAmount(amount)}</p>
          {hasCategories && <p className="text-muted-foreground">{budgetUnassignedCount(categoryCount)}</p>}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        asChild
        // On a phone the link wraps under the message: ml-4 plus the button's own padding lines its label up with
        // the text column (16px icon + 12px gap).
        className="ml-4 h-11 shrink-0 self-start text-muted-foreground hover:bg-muted hover:text-foreground sm:ml-0 sm:h-9 sm:self-auto"
      >
        <Link to={hasCategories ? ROUTES.categoriesPending : ROUTES.categories}>
          {hasCategories ? BUDGET_CLASSIFY_ACTION : BUDGET_VIEW_CATEGORIES}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </Button>
    </div>
  );
};
